// How the home has grown (P81): a small brass-edged plaque at the head of
// the house page's story, from /api/house/<slug>/made: what the home has
// now (rooms, kinds growing, parts built by hand, aims finished), its last
// few milestones with their days, the aims under way in calm words, and the
// way to "Things made here" (/@slug/made), which is also a row in the drawer.
// Public fields only; nothing here counts down or asks for anything.
import type { Shell } from './shell.js';

type Made = {
  name: string; page: string;
  grown: { words: string; since?: number; timeline: { day: number; text: string; emoji: string; at: number }[] };
  aims: { open: { aim: string; progress: string; own: boolean }[]; done: { done: string; day: number }[] };
};

const el = (tag: string, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };

let styled = false;
function style() {
  if (styled) return; styled = true;
  const s = document.createElement('style');
  s.textContent = `
.pz-grown{margin:0 0 10px;padding:9px 11px 8px;background:var(--pz-paper-2,#f5ecd9);border:1px solid var(--pz-rule,#e2d4ba);border-left:3px solid var(--pz-brass,#c9a24a);border-radius:3px}
.pz-grown:empty{display:none}
.pz-grown h4{margin:0;font-size:13.5px;font-weight:800}
.pz-grown .w{font-size:12.5px;color:var(--pz-ink-2,#6e5a48);margin:1px 0 5px}
.pz-grown ol{list-style:none;margin:0;padding:0;display:flex;flex-wrap:wrap;gap:4px 6px}
.pz-grown ol li{font-size:12px;background:var(--pz-paper,#fffaf0);border:1px solid var(--pz-rule,#e2d4ba);border-radius:3px;padding:1px 6px;white-space:nowrap;max-width:100%;overflow:hidden;text-overflow:ellipsis}
.pz-grown ol li b{font-weight:800;color:var(--pz-ink-3,#9a8670);margin-right:3px}
.pz-grown .aim{font-size:12.5px;margin:6px 0 0;color:var(--pz-ink,#3a2a20)}
.pz-grown .aim span{color:var(--pz-ink-2,#6e5a48)}
@media (max-width:600px){.pz-grown ol li:nth-child(n+3){display:none}}
.pz-grown a{display:inline-block;margin-top:6px;font-size:12.5px;font-weight:800;color:#8a5a14}`;
  document.head.append(s);
}

// the line for a README, as plazmi.card-svg/snippet writes it on /claim and /@slug/made
async function copyCard(sh: Shell, name: string, slug: string) {
  const base = location.origin;
  const md = `[![${name.replace(/[[\]\\`]/g, '\\$&')} on Plazmi](${base}/@${slug}/card.svg)](${base}/join/${slug})`;
  try {
    await navigator.clipboard.writeText(md);
    sh.toast('Copied. It goes in a README as it is.');
  } catch { window.prompt('This home\'s live card, for a GitHub README:', md); }
}

export function growthStrip(sh: Shell, slug: string) {
  style();
  const box = el('div', 'pz-grown');
  box.dataset.grown = slug;
  let key = '';
  const draw = (m: Made) => {
    const k = JSON.stringify([m.grown.words, m.grown.timeline.slice(0, 4), m.aims.open]);
    if (k === key) return; key = k;
    box.innerHTML = '';
    box.append(el('h4', '', 'How the home has grown'), el('p', 'w', `${m.grown.words}${m.grown.since ? ` · since Day ${m.grown.since}` : ''}`));
    const ol = el('ol');
    for (const t of m.grown.timeline.slice(0, 4)) { const li = el('li'); li.title = t.text; li.append(el('b', '', `Day ${t.day}`), `${t.emoji} ${t.text}`); ol.append(li); }
    if (ol.children.length) box.append(ol);
    for (const a of m.aims.open) {
      const p = el('p', 'aim'); p.append(`🧭 ${m.name} aims to ${a.aim}`, el('span', '', ` · ${a.progress}`)); box.append(p);
    }
    const go = el('a', '', 'Things made here →') as HTMLAnchorElement; go.href = m.page; box.append(go);
    sh.go('made', 'Things made here', [
      { label: `Things made at ${m.name}'s`, sub: 'Dances, the garden, the guestbook, postcards', href: m.page },
      { label: 'For a GitHub README', sub: 'Copies this home\'s live card, as a line of Markdown', onClick: () => copyCard(sh, m.name, slug) },
    ], 65);
  };
  const refresh = async () => {
    try {
      const r = await fetch(`/api/house/${encodeURIComponent(slug)}/made`);
      if (r.ok) draw(await r.json());
    } catch { /* the strip waits for the next try */ }
  };
  refresh();
  return { el: box, refresh };
}
